import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { LoadingStatus } from 'src/app/core/domain/events/state_provider';

import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { DietitianCommands } from '../../../../../../data/dietitian/domain/dietitian_commands';
import { DietitianStateProvider } from '../../../../../../data/dietitian/domain/dietitian_state_provider';
import { PatientGroupEventProvider } from '../../../../../../data/dietitian/domain/events/patient_group_event_provider';
import { PatientGroupEvent } from '../../../../../../data/dietitian/domain/events/patient_group_events';
import { SurveyEventProvider } from '../../../../../../data/dietitian/domain/events/survey/survey_event_provider';
import { SurveyEvent } from '../../../../../../data/dietitian/domain/events/survey/survey_events';
import { PatientGroup } from '../../../../../../data/dietitian/domain/patient_group/patient_group';
import { PatientGroupCommands } from '../../../../../../data/dietitian/domain/patient_group/patient_group_commands';
import {
  PatientSelectionActivated,
  PatientSelectionArchived,
  PatientSelectionEvent,
} from '../../../../../../data/patient/domain/events/patient_selection_event';
import { PatientSelectionEventProvider } from '../../../../../../data/patient/domain/events/patient_selection_event_provider';
import {
  Patient,
  PatientProps,
} from '../../../../../../data/patient/domain/patient';
import { PatientCommands } from '../../../../../../data/patient/domain/patient_commands';
import { PatientStateProvider } from '../../../../../../data/patient/domain/patient_state_provider';
import { SortListType } from '../../questions.service';

export interface PatientsListViewModel {
  isLoading: boolean;
  error: string;
  patients: Patient[];
}

export interface AccountViewModel {
  isLoading: boolean;
  error: string;
  dietitian: Dietitian;
}

@Injectable()
export class PatientsListFacade {
  constructor(
    private patientStateProvider: PatientStateProvider,
    private dietitianStateProvider: DietitianStateProvider,
    private surveyEventProvider: SurveyEventProvider,
    private patientCommands: PatientCommands,
    private dietitianCommands: DietitianCommands,
    private patientGroupCommands: PatientGroupCommands,
    private patientGroupEventProvider: PatientGroupEventProvider,
    private patientSelectionEventProvider: PatientSelectionEventProvider,
  ) {
    // do nothing
    this.patientSelectionEvent$.subscribe((event) => {
      if (event instanceof PatientSelectionArchived) {
        this.dietitianCommands.onPatientSelectionArchived(event).then();
      }
      if (event instanceof PatientSelectionActivated) {
        this.dietitianCommands.onPatientSelectionActivated(event).then();
      }
    });
  }

  get patientGroupEvents$(): Observable<PatientGroupEvent> {
    return this.patientGroupEventProvider.events$;
  }

  get surveyEvents$(): Observable<SurveyEvent> {
    return this.surveyEventProvider.events$;
  }

  get patientSelectionEvent$(): Observable<PatientSelectionEvent> {
    return this.patientSelectionEventProvider.events$;
  }

  get patientsStatus$(): Observable<PatientsListViewModel> {
    return this.patientStateProvider.state$.pipe(
      map((e) => {
        return <PatientsListViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          patients: [e.entity],
        };
      }),
    );
  }

  get accountStatus$(): Observable<AccountViewModel> {
    return this.dietitianStateProvider.state$.pipe(
      map((e) => {
        return <AccountViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          dietitian: e.entity,
        };
      }),
    );
  }

  public searchDietitianPatients(
    dietId: string,
    query: string,
    filterType: SortListType,
    groupId: string | null,
    resultsPerPage: number,
    page: number,
    sort: 'createdAt' | 'updatedAt' | 'lastActiveDate',
    algolia: boolean,
  ) {
    console.log('☢️ searchDietitianPatients', { algolia });
    return this.patientCommands.searchDietitianPatients(
      dietId,
      query,
      filterType,
      groupId,
      resultsPerPage,
      page,
      sort,
      algolia,
    );
  }

  public archivePatient(patientId: string) {
    return this.patientCommands.updatePatient(patientId, {
      archivedAt: new Date(),
    } as PatientProps);
  }

  public async archiveAllPatients(patients: Patient[] | undefined) {
    const cnt = await this.patientCommands.archiveAllPatients(patients);
    cnt.forEach(async (value, groupId) => {
      const group = await this.patientGroupCommands.getPatientGroup(groupId);
      await this.patientGroupCommands.savePatientGroup(
        group.updatePatientCount(
          group.patientCount - value,
          group.activatedPatientCount - value,
          group.archivedPatientCount + value,
        ),
        group.id.toString(),
      );
    });
  }

  public activePatient(patientId: string) {
    return this.patientCommands.updatePatient(patientId, {
      archivedAt: undefined,
    } as PatientProps);
  }

  public activeAllPatients(patients: Patient[] | undefined) {
    return this.patientCommands.activeAllPatients(patients);
  }

  public getDietitianGroups(dietitianId: string): Promise<PatientGroup[]> {
    return this.patientGroupCommands.getDietitianPatientGroups(dietitianId);
  }

  public updateDietitian(dietitian: Dietitian) {
    return this.dietitianCommands.saveDietitian(dietitian);
  }

  public async getAllArchivedPatients(dietitianId: string): Promise<Patient[]> {
    return this.patientCommands.getAllArchivedPatients(dietitianId);
  }
}
